import { CONNECTOR_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input } from "@sagot/ui";
import { useId, useState } from "react";
import { recordOf, rowProblems, rowsOf, type Row, type RowKind } from "./connector-model";

const C = "engage.workflows.connector";

const sameRecord = (a: Readonly<Record<string, string>> | undefined, b: Readonly<Record<string, string>> | undefined): boolean => {
  const left = Object.entries(a ?? {});
  const right = b ?? {};
  return left.length === Object.keys(right).length && left.every(([key, value]) => right[key] === value);
};

/**
 * Query parameters and headers while they are edited. A row may be half written (no name yet), so the rows live here and
 * the step only gets the finished ones; a change that comes from outside (undo, a restored version) replaces the rows.
 */
export function useRows(record: Readonly<Record<string, string>> | undefined, commit: (record: Record<string, string> | undefined) => void) {
  const [rows, setRows] = useState<Row[]>(() => rowsOf(record));
  const [seen, setSeen] = useState(record);
  if (record !== seen) {
    setSeen(record);
    if (!sameRecord(recordOf(rows), record)) setRows(rowsOf(record));
  }
  const change = (next: Row[]) => {
    setRows(next);
    commit(recordOf(next));
  };
  return { rows, change };
}

interface RowsEditorProps {
  kind: RowKind;
  label: string;
  hint?: string;
  addLabel: string;
  namePlaceholder: string;
  valuePlaceholder?: string;
  rows: Row[];
  disabled: boolean;
  onChange: (rows: Row[]) => void;
}

/** A list of name / value pairs: a query parameter or a header of the request. */
export function RowsEditor({ kind, label, hint, addLabel, namePlaceholder, valuePlaceholder, rows, disabled, onChange }: RowsEditorProps) {
  const { t } = useTranslation();
  const id = useId();
  const max = kind === "header" ? CONNECTOR_LIMITS.headerRows : CONNECTOR_LIMITS.queryRows;
  const problems = rowProblems(rows, kind);
  const update = (index: number, patch: Partial<Row>) => { onChange(rows.map((row, at) => (at === index ? { ...row, ...patch } : row))); };
  return (
    <fieldset className="space-y-2" data-testid={`connector-${kind}-rows`}>
      <legend className="label">{label}</legend>
      {hint ? <p className="hint -mt-1">{hint}</p> : null}
      {rows.map((row, index) => {
        const problem = problems[index];
        const errorId = `${id}-${String(index)}-error`;
        return (
          <div key={index} className="space-y-1">
            <div className="flex items-start gap-2">
              <Input aria-label={`${label}: ${t(`${C}.rows.name`)} ${String(index + 1)}`} aria-describedby={problem ? errorId : undefined} invalid={problem != null} className="flex-1 font-mono text-xs" placeholder={namePlaceholder} disabled={disabled} maxLength={kind === "header" ? CONNECTOR_LIMITS.headerName : CONNECTOR_LIMITS.queryKey} value={row.key} onChange={(event) => { update(index, { key: event.target.value }); }} />
              <Input aria-label={`${label}: ${t(`${C}.rows.value`)} ${String(index + 1)}`} className="flex-1 font-mono text-xs" placeholder={valuePlaceholder} disabled={disabled} maxLength={kind === "header" ? CONNECTOR_LIMITS.headerValue : CONNECTOR_LIMITS.queryValue} value={row.value} onChange={(event) => { update(index, { value: event.target.value }); }} />
              <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={`${t(`${C}.rows.remove`)} ${label} ${String(index + 1)}`} onClick={() => { onChange(rows.filter((_, at) => at !== index)); }}>
                <Icon name="trash" />
              </Button>
            </div>
            {problem ? <p id={errorId} role="alert" className="text-xs font-semibold text-danger">{t(`${C}.problems.row.${problem}`)}</p> : null}
          </div>
        );
      })}
      {rows.length < max ? (
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => { onChange([...rows, { key: "", value: "" }]); }}>
          <Icon name="plus" />
          {addLabel}
        </Button>
      ) : null}
    </fieldset>
  );
}
